import { ObjectBuilder, Image3D, ImgButton, MeshUtils, Rectangle3D } from "@dreamcam/babylon-tools";
import { AppView } from "../AppView";
import { Env } from "../../settings/Env";
import { createVector3 } from "../ButtonAnimation";
import { MeshName } from "../MeshName";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { Observable } from "@babylonjs/core/Misc/observable.js";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { TextWrapping } from "@babylonjs/gui/2D/controls/textBlock.js";
import { MessageType } from "../../interfaces/NotificationCenter";
class NotificationItem {
    constructor(name, position) {
        this._id = -1;
        this.closeObservable = new Observable();
        const appView = AppView.getInstance();
        const s = Env.notificationCenter;
        const width = s.background.width;
        const height = s.background.height;
        this._mesh2D = MeshUtils.createPlaneMesh(name, width, height, appView.scene, appView.guiRootNode, DcRenderingGroup.topmostDialogs);
        this._mesh2D.mesh.position = position;
        MeshUtils.hide(this._mesh2D);
        const container = new Container("item");
        container.widthInPixels = this._mesh2D.m2px(width);
        container.heightInPixels = this._mesh2D.m2px(height);
        const backgroundPanel = new Rectangle3D("notificationCenterPanelBackground");
        backgroundPanel.widthInPixels = this._mesh2D.m2px(s.background.width);
        backgroundPanel.heightInPixels = this._mesh2D.m2px(s.background.height);
        backgroundPanel.paddingTopInPixels = 1; // Babylon-js bug workaround.
        backgroundPanel.cornerRadius = this._mesh2D.m2px(s.background.cornerRadius);
        backgroundPanel.background = s.background.backgroundColor;
        backgroundPanel.color = s.background.borderColor;
        backgroundPanel.thickness = this._mesh2D.m2px(s.background.thickness);
        container.addControl(backgroundPanel);
        const horMarginPx = this._mesh2D.m2px(s.margin);
        const verMarginPx = this._mesh2D.m2px(s.margin);
        const addIcon = (name, path) => {
            const image = new Image3D(name, path);
            image.widthInPixels = this._mesh2D.m2px(s.icon.width);
            image.heightInPixels = this._mesh2D.m2px(s.icon.height);
            image.leftInPixels = -container.widthInPixels / 2 + horMarginPx + image.widthInPixels / 2;
            image.topInPixels = -container.heightInPixels / 2 + verMarginPx + image.heightInPixels / 2;
            container.addControl(image);
            return image;
        };
        this._infoIcon = addIcon("info_icon", s.icon.info);
        this._warningIcon = addIcon("warning_icon", s.icon.warning);
        this._errorIcon = addIcon("error_icon", s.icon.error);
        const closeButton = new ImgButton("close", s.closeButtonLayout);
        closeButton.widthInPixels = this._mesh2D.m2px(s.closeButtonLayout.width);
        closeButton.heightInPixels = this._mesh2D.m2px(s.closeButtonLayout.height);
        closeButton.leftInPixels = container.widthInPixels / 2 - horMarginPx - closeButton.widthInPixels / 2;
        closeButton.topInPixels = -container.heightInPixels / 2 + verMarginPx + closeButton.heightInPixels / 2;
        closeButton.onClickObserver.add(() => this.closeObservable.notifyObservers(this._id));
        container.addControl(closeButton);
        this._textBlock = ObjectBuilder.createTextBlock3D(this._mesh2D, "text", "", {
            height: s.background.height - 2 * s.margin,
            width: s.background.width - s.margin * 4 - s.closeButtonLayout.width - s.icon.width,
            fontSize: s.fontSize,
            fontWeight: s.fontWeight,
            color: s.textColor
        });
        this._textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._textBlock.textWrapping = TextWrapping.WordWrap;
        container.addControl(this._textBlock);
        this._mesh2D.texture.addControl(container);
    }
    update(item) {
        this._id = item.id;
        this._infoIcon.isVisible = item.messageType === MessageType.info;
        this._warningIcon.isVisible = item.messageType === MessageType.warning;
        this._errorIcon.isVisible = item.messageType === MessageType.error;
        this._textBlock.text = item.text;
    }
    get isVisible() {
        return this._mesh2D.mesh.isVisible;
    }
    set isVisible(value) {
        MeshUtils.setVisibility(this._mesh2D, value);
    }
}
export class NotificationCenterComponent {
    constructor() {
        this._items = [];
        this._idGenerator = 0;
        this._onClose = (id) => {
            const index = this._items.findIndex((item) => item.id === id);
            if (index < 0)
                return;
            this._items.splice(index, 1);
            setTimeout(() => {
                this.draw();
            }, 0);
        };
        const s = Env.notificationCenter;
        this._notificationItems = [];
        for (let i = 0; i < s.maxNumberOfMessages; i++) {
            const position = createVector3(s.position);
            position.y += i * (s.gapBetweenPanels + s.background.height);
            const notificationItem = new NotificationItem(MeshName.notificationCenter, position);
            notificationItem.closeObservable.add(this._onClose);
            this._notificationItems.push(notificationItem);
        }
        // (() => {
        //     setTimeout(() => {
        //         this.showMessage(MessageType.warning, "First warning");
        //     }, 10000);
        //     setTimeout(() => {
        //         this.showMessage(MessageType.error, "First error");
        //     }, 12000);
        //     setTimeout(() => {
        //         this.showMessage(MessageType.warning, "Second warning");
        //     }, 13000);
        //     setTimeout(() => {
        //         this.showMessage(MessageType.warning, "First warning must disappear now. This warning must be at the bottom.");
        //     }, 14000);
        // })();
    }
    showMessage(messageType, text) {
        if (this._items.length === Env.notificationCenter.maxNumberOfMessages) {
            this._items.shift();
        }
        const item = {
            id: ++this._idGenerator,
            messageType,
            text
        };
        this._items.push(item);
        const timeout = Env.notificationCenter.timeout;
        setTimeout(() => this._onClose(item.id), messageType === MessageType.error ? timeout.errorMs : (messageType === MessageType.warning ? timeout.warningMs : timeout.infoMs));
        this.draw();
    }
    draw() {
        for (let i = 0; i < Env.notificationCenter.maxNumberOfMessages; i++) {
            const notificationItem = this._notificationItems[i];
            notificationItem.isVisible = i < this._items.length;
            if (notificationItem.isVisible) {
                notificationItem.update(this._items[this._items.length - i - 1]);
            }
        }
    }
}
